import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteCampaignDraft } from "@/api/campaignBuilder";
import { cloneCampaign } from "@/api/campaigns";
import ToastChip from "@/components/campaignBuilder/ToastChip";
import CampaignCard from "@/components/campaigns/CampaignCard";
import { PermissionGate } from "@/components/PermissionGate";
import { releaseCampaignBuilderDraft } from "@/constants/campaignBuilder";
import { CAMPAIGN_STATUSES, CAMPAIGN_WRITE_ERROR } from "@/constants/campaings";
import { useCampaigns } from "@/hooks/useCampaigns";
import { useIsMobile } from "@/hooks/useMediaQuery";
import type { Campaign } from "@/interfaces/campaing";
import type { CampaignStatus, ClonedEntry } from "@/types/types";
import { invalidateCampaignReads } from "@/utils/campaignCache-helper";
import { writeDenialMessage } from "@/utils/saveError-helper";

const STATUS_OPTIONS: { value: CampaignStatus | "all"; label: string }[] = [
  { value: "all", label: "All Statuses" },
  { value: CAMPAIGN_STATUSES.DRAFT, label: "Draft" },
  { value: CAMPAIGN_STATUSES.SCHEDULED, label: "Scheduled" },
  { value: CAMPAIGN_STATUSES.ACTIVE, label: "Active" },
  { value: CAMPAIGN_STATUSES.CLOSED, label: "Closed" },
];

export default function Campaigns() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | "all">("all");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: campaigns = [], isLoading } = useCampaigns({
    search: debouncedSearch || undefined,
    status: statusFilter,
  });

  const [openAnalyticsCampaignId, setOpenAnalyticsCampaignId] = useState<string | null>(null);
  const [clonedEntries, setClonedEntries] = useState<ClonedEntry[]>([]);
  // Optimistically hide a soft-deleted campaign; the invalidated list refetch (archived excluded)
  // makes the removal permanent, or we restore the id if the delete fails.
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  // A card action (clone / delete / rename) that the API refuses — shown as a wine pill rather than
  // reverting the card silently. Row-scope is an API-only gate (the UI can't see it), so a user who
  // is shown an action they can't actually perform gets told why instead of a dead click.
  const [actionError, setActionError] = useState<string | null>(null);

  const handleAnalyticsToggle = useCallback((id: string) => {
    setOpenAnalyticsCampaignId((prev) => (prev === id ? null : id));
  }, []);

  const handleClone = useCallback(
    async (source: Campaign) => {
      const tempId = `clone-${Date.now()}`;
      const placeholder: Campaign = {
        ...source,
        id: tempId,
        name: `Copy – ${source.name}`,
        status: CAMPAIGN_STATUSES.DRAFT,
        progress: 0,
        daysLeft: undefined,
        alertCount: 0,
        reminderDaysLeft: undefined,
        reminder1Date: null,
        reminder2Date: null,
        reminder3Date: null,
        reminder4Date: null,
      };
      setClonedEntries((prev) => [{ tempId, data: placeholder, loading: true }, ...prev]);
      try {
        const result = await cloneCampaign(source.id);
        setClonedEntries((prev) =>
          prev.map((e) =>
            e.tempId === tempId ? { tempId: result.id, data: result, loading: false } : e,
          ),
        );
        invalidateCampaignReads(queryClient);
      } catch (err) {
        setClonedEntries((prev) => prev.filter((e) => e.tempId !== tempId));
        setActionError(writeDenialMessage(err, CAMPAIGN_WRITE_ERROR.clone));
      }
    },
    [queryClient],
  );

  const handleDelete = useCallback(
    async (campaign: Campaign) => {
      setDeletedIds((prev) => new Set(prev).add(campaign.id));
      try {
        await deleteCampaignDraft(campaign.id);
        invalidateCampaignReads(queryClient);
      } catch (err) {
        // Restore the card on failure.
        setDeletedIds((prev) => {
          const next = new Set(prev);
          next.delete(campaign.id);
          return next;
        });
        setActionError(writeDenialMessage(err, CAMPAIGN_WRITE_ERROR.delete));
      }
    },
    [queryClient],
  );

  const handleCreateCampaign = useCallback(() => {
    // "+" means NEW: drop any stashed campaign so the builder does not reopen the last one.
    releaseCampaignBuilderDraft();
    navigate("/campaign/create");
  }, [navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // Fill content area: 100vh minus app bar (64px) and main's top+bottom padding (24px × 2)
        height: "calc(100vh - 112px)",
        overflow: "hidden",
      }}
    >
      {/* Sub-header: stays in place while the list below scrolls */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: "var(--color-background)",
          px: isMobile ? 0 : 3,
          pb: 2,
        }}
      >
        <Typography variant="body1" sx={{ color: "var(--color-rodeo-black)", mb: 2.5 }}>
          Your Campaigns ({isLoading ? "…" : campaigns.length})
        </Typography>

        <Stack
          direction={isMobile ? "column" : "row"}
          alignItems={isMobile ? "stretch" : "center"}
          gap={1.5}
          sx={{ width: "100%" }}
        >
          {/* Search input — full width on all sizes */}
          <TextField
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "var(--color-graphite)", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: isMobile ? "100%" : 360,
              "& .MuiOutlinedInput-root": {
                height: 52,
                borderRadius: "var(--radius-btn)",
                bgcolor: "background.paper",
                "& fieldset": { borderColor: "var(--color-smoke)" },
                "&:hover fieldset": { borderColor: "var(--color-rodeo-dust)" },
                "&.Mui-focused fieldset": {
                  borderColor: "var(--color-dark-dust)",
                },
              },
              "& .MuiInputBase-input": {
                fontFamily: "var(--font-lato)",
                fontSize: 16,
                color: "var(--color-rodeo-black)",
                "&::placeholder": { color: "var(--color-graphite)", opacity: 1 },
              },
            }}
          />

          {/* Status filter + create button row */}
          <Stack direction="row" alignItems="center" gap={1.5} sx={{ flex: 1 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as CampaignStatus | "all")}
              displayEmpty
              IconComponent={ExpandMoreIcon}
              sx={{
                flex: isMobile ? 1 : "0 0 220px",
                height: 52,
                borderRadius: "var(--radius-btn)",
                bgcolor: "background.paper",
                fontFamily: "var(--font-lato)",
                fontSize: 16,
                color: "var(--color-rodeo-black)",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--color-smoke)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--color-rodeo-dust)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--color-dark-dust)",
                },
                "& .MuiSelect-icon": {
                  color: "var(--color-graphite)",
                },
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  sx={{
                    fontFamily: "var(--font-lato)",
                    fontSize: 14,
                    color: "var(--color-rodeo-black)",
                  }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Select>

            {/* Create new campaign button */}
            <PermissionGate action="create" subject="CAMPAIGN">
              <IconButton
                onClick={handleCreateCampaign}
                aria-label="Create new campaign"
                sx={{
                  ml: isMobile ? 0 : "auto",
                  width: 52,
                  height: 52,
                  border: "1px solid var(--color-smoke)",
                  borderRadius: "var(--radius-btn)",
                  bgcolor: "background.paper",
                  color: "var(--color-rodeo-black)",
                  flexShrink: 0,
                  "&:hover": {
                    bgcolor: "var(--color-light-dust)",
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </PermissionGate>
          </Stack>
        </Stack>
      </Box>

      {/* Campaign list — scrollable area */}
      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, px: isMobile ? 0 : 3, py: 2.5 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress
              aria-label="Loading"
              size={32}
              sx={{ color: "var(--color-dark-dust)" }}
            />
          </Box>
        ) : campaigns.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 8,
              gap: 1,
            }}
          >
            <Typography
              variant="h4"
              sx={{ color: "var(--color-graphite)", fontFamily: "var(--font-crimson)" }}
            >
              No campaigns found
            </Typography>
            <Typography variant="body2" sx={{ color: "var(--color-graphite)" }}>
              {debouncedSearch || statusFilter !== "all"
                ? "Try adjusting your search or filter."
                : "Create your first campaign to get started."}
            </Typography>
          </Box>
        ) : (
          <Stack gap={3}>
            {[
              // Once a refetch carries the clone, the server row wins — otherwise the same id
              // renders twice (duplicate React key) after any later invalidation.
              ...clonedEntries
                .filter((e) => !campaigns.some((c) => c.id === e.data.id))
                .map((e) => ({ campaign: e.data, loading: e.loading })),
              ...campaigns.map((c) => ({ campaign: c, loading: false })),
            ]
              .filter(({ campaign }) => !deletedIds.has(campaign.id))
              .map(({ campaign, loading }) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  isLoading={loading}
                  analyticsOpen={openAnalyticsCampaignId === campaign.id}
                  onAnalyticsToggle={() => handleAnalyticsToggle(campaign.id)}
                  onClone={handleClone}
                  onDelete={handleDelete}
                  onActionError={setActionError}
                />
              ))}
          </Stack>
        )}
      </Box>

      <ToastChip
        variant="error"
        message={actionError ?? ""}
        open={actionError !== null}
        onClose={() => setActionError(null)}
      />
    </Box>
  );
}
